import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Container,
  Grid,
  Paper,
  Typography,
  Card,
  CircularProgress,
  Alert,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Description as DescriptionIcon,
  School as SchoolIcon,
  Feedback as FeedbackIcon,
  Pending as PendingIcon,
  Group as GroupIcon,
  Security as SecurityIcon,
  Policy as PolicyIcon,
} from '@mui/icons-material';
import { motion, AnimatePresence, MotionConfig } from 'motion/react';

// Tab Components
import DocumentsTab from '../components/existing/DocumentsTab';
import TrainingTab from '../components/existing/TrainingTab';
import FeedbackTab from '../components/existing/FeedbackTab';
import KpikriTab from '../components/existing/KpikriTab';
import KpiProposalTab from '../components/existing/KpiProposalTab';
import BrandSurveyTab from '../components/existing/BrandSurveyTab';
import GrievanceFormTab from '../components/existing/GrievanceFormTab';
import PoshComplaintTab from '../components/existing/PoshComplaintTab';
import MyTeamTab from '../components/existing/MyTeamTab';
import AccessProvisioningTab from '../components/existing/AccessProvisioningTab';
import ResourceTab from '../components/existing/ResourceTab';
import UpdatedPoliciesTab from '../components/existing/UpdatedPoliciesTab';
import { useAuth } from '../context/AuthContext';

// Motion-enabled MUI components (presentation only)
const MotionBox = motion.create(Box);
const MotionPaper = motion.create(Paper);
const MotionCard = motion.create(Card);
const MotionTypography = motion.create(Typography);

const fadeUp = {
  hidden: { opacity: 0, y: 16 },
  visible: { opacity: 1, y: 0 },
};

const easeOutExpo = [0.22, 1, 0.36, 1];

// Directional slide for the tab panel. `direction` is +1 when moving to a card
// further right in the list, -1 when moving left, 0 for deep links (fade only).
const tabPanelVariants = {
  enter: (direction) => ({ opacity: 0, x: direction * 64 }),
  center: { opacity: 1, x: 0 },
  exit: (direction) => ({ opacity: 0, x: direction * -64 }),
};

const ExistingEmployee = () => {
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedOption, setSelectedOption] = useState('documents');
  // Presentation only: which way the tab panel should slide on the next change.
  const [slideDirection, setSlideDirection] = useState(0);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const { user } = useAuth();

  const options = [
    { key: 'documents', label: 'Documents', icon: <DescriptionIcon />, component: DocumentsTab },
    { key: 'updated_policies', label: 'Updated Policies', icon: <PolicyIcon />, component: UpdatedPoliciesTab },
    { key: 'training', label: 'Training', icon: <SchoolIcon />, component: TrainingTab },
    { key: 'feedback', label: 'Feedback', icon: <FeedbackIcon />, component: FeedbackTab },
    { key: 'my_team', label: 'My Team', icon: <GroupIcon />, component: MyTeamTab },
    { key: 'kpi_kri', label: 'KPI, KRA', icon: <PendingIcon />, component: KpikriTab },
    { key: 'kpi_proposal', label: 'Propose KPIs', icon: <PendingIcon />, component: KpiProposalTab },
    { key: 'brand_survey', label: 'Brand Survey', icon: <PendingIcon />, component: BrandSurveyTab , disabled: true },
    { key: 'grievance_form', label: 'Grievance Form', icon: <PendingIcon />, component: GrievanceFormTab },
    { key: 'posh_complaint', label: 'POSH Complaint', icon: <PendingIcon />, component: PoshComplaintTab },
    { key: 'access_provisioning', label: 'Access Provisioning', icon: <SecurityIcon />, component: AccessProvisioningTab },
    { key: 'resource', label: 'Resources', icon: <PendingIcon />, component: ResourceTab },
  ];

  // Sets the slide direction from the card positions, then selects as before.
  const handleSelectOption = (key) => {
    const from = options.findIndex((o) => o.key === selectedOption);
    const to = options.findIndex((o) => o.key === key);
    setSlideDirection(to > from ? 1 : -1);
    setSelectedOption(key);
  };

  useEffect(() => {
    const fetchEmployeeData = async () => {
      try {
        setLoading(true);
        await new Promise((resolve) => setTimeout(resolve, 1000));
      } catch (err) {
        setError('Failed to load employee data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchEmployeeData();
  }, []);

  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam && options.some(option => option.key === tabParam && !option.disabled)) {
      setSelectedOption(tabParam);
    }
  }, [searchParams]);

  if (loading) {
    return (
      <MotionBox
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '400px',
        }}
      >
        <motion.div
          animate={{ scale: [1, 1.08, 1] }}
          transition={{ duration: 1.6, repeat: Infinity, ease: 'easeInOut' }}
          style={{ display: 'flex' }}
        >
          <CircularProgress />
        </motion.div>
      </MotionBox>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <motion.div
          initial={{ opacity: 0, y: -12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ type: 'spring', stiffness: 260, damping: 24 }}
        >
          <Alert severity="error">{error}</Alert>
        </motion.div>
      </Container>
    );
  }

  // If no user is found yet
  if (!user) return <div>Loading...</div>;

  return (
    <MotionConfig reducedMotion="user">
    <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1, p: { xs: 2, sm: 3 } }}>
      <Container maxWidth={false} disableGutters sx={{ width: '100%' }}>
      <Box sx={{ mb: 4, mt: 2 }}>
        <MotionTypography
          gutterBottom
          variants={fadeUp}
          initial="hidden"
          animate="visible"
          transition={{ duration: 0.5, ease: easeOutExpo }}
          sx={{ fontWeight: 600, color: 'text.primary', letterSpacing: 1 ,fontSize: 30 }}
        >
          Welcome, {user.firstName}  {user.lastName}, to Divergent Insights!
        </MotionTypography>
        <MotionTypography
          color="text.secondary"
          gutterBottom
          variants={fadeUp}
          initial="hidden"
          animate="visible"
          transition={{ duration: 0.5, delay: 0.1, ease: easeOutExpo }}
          sx={{ fontStyle: 'italic', fontWeight: 400, mb: 3 ,fontSize: 20}}
        >
        Creating Value Through Collaboration
        </MotionTypography>
      </Box>
        <MotionPaper
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55, delay: 0.15, ease: easeOutExpo }}
          sx={{ p: { xs: 2, sm: 4 }, width: '100%' }}
        >
          {/* <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
            Employee Portal
          </Typography> */}
          <MotionTypography
            variant="subtitle1"
            color="text.secondary"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            sx={{ mb: 4, fontSize: '0.9rem' }}
          >
          This section allows easy access to managing your documents, trainings, share feedback, raise grievances with ease and POSH-compliant support to ensure a safe and supportive workplace.
          </MotionTypography>

          {/* Option Cards */}
          <Grid
            container
            spacing={2}
            justifyContent={isMobile ? 'center' : 'flex-start'}
            sx={{ mb: 4 }}
          >
            {options.map((option, index) => (
              <Grid item xs={6} sm="auto" key={option.key}>
                <MotionCard
                  initial={{ opacity: 0, y: 20, scale: 0.96 }}
                  animate={{ opacity: option.disabled ? 0.6 : 1, y: 0, scale: 1 }}
                  transition={{
                    delay: 0.35 + index * 0.045,
                    type: 'spring',
                    stiffness: 320,
                    damping: 26,
                  }}
                  whileHover={option.disabled ? undefined : { y: -6, scale: 1.03 }}
                  whileTap={option.disabled ? undefined : { scale: 0.97 }}
                  sx={{
                    minWidth: 160,
                    minHeight: 130,
                    boxShadow: selectedOption === option.key ? 6 : 1,
                    backgroundColor: option.disabled
                      ? 'grey.100'
                      : selectedOption === option.key
                        ? 'primary.100'
                        : 'background.paper',
                    color: option.disabled
                      ? 'grey.500'
                      : selectedOption === option.key
                        ? 'primary.main'
                        : 'text.primary',
                    border: '1px solid #e0e0e0',
                    transition: 'background-color 0.2s, box-shadow 0.2s, color 0.2s',
                    cursor: option.disabled ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textAlign: 'center',
                    px: 1,
                    opacity: option.disabled ? 0.6 : 1,
                    position: 'relative',
                  }}
                  onClick={() => !option.disabled && handleSelectOption(option.key)}
                >
                  {/* Shared-layout ring: physically flies from the previously
                      selected card to this one instead of popping on/off. */}
                  {selectedOption === option.key && (
                    <motion.div
                      layoutId="existing-employee-card-highlight"
                      transition={{ type: 'spring', stiffness: 420, damping: 34 }}
                      style={{
                        position: 'absolute',
                        inset: 0,
                        borderRadius: 'inherit',
                        border: '2px solid #1976d2',
                        boxShadow: 'inset 0 0 0 4px rgba(25, 118, 210, 0.10)',
                        pointerEvents: 'none',
                      }}
                    />
                  )}
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 2, position: 'relative' }}>
                    <motion.div
                      animate={
                        selectedOption === option.key
                          ? { scale: [1, 1.25, 1], rotate: [0, -6, 0] }
                          : { scale: 1, rotate: 0 }
                      }
                      transition={{ duration: 0.45, ease: 'easeOut' }}
                      style={{ display: 'flex' }}
                    >
                      {option.icon}
                    </motion.div>
                    <Typography variant="subtitle2" sx={{ mt: 1, fontWeight: 500,fontSize: '0.9rem' }}>
                      {option.label}
                    </Typography>
                  </Box>
                </MotionCard>
              </Grid>
            ))}
          </Grid>

          {/* Selected Tab Content */}
          <Box sx={{ mt: 2, overflowX: 'hidden' }}>
            <AnimatePresence mode="wait" initial={false} custom={slideDirection}>
              <motion.div
                key={selectedOption}
                custom={slideDirection}
                variants={tabPanelVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ duration: 0.26, ease: easeOutExpo }}
              >
                {options.find((o) => o.key === selectedOption)?.component &&
                  React.createElement(options.find((o) => o.key === selectedOption).component)}
              </motion.div>
            </AnimatePresence>
          </Box>
        </MotionPaper>
      </Container>
    </Box>
    </MotionConfig>
  );
};

export default ExistingEmployee;
